import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowUpRight,
  FiArrowDownRight,
  FiClock,
  FiSearch,
  FiDownload,
  FiInbox,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiChevronDown,
} from 'react-icons/fi';
import {
  useOfferPaymentsList,
  useOfferPaymentsCsvExport,
  type OfferPaymentHistoryItem,
  type OfferPaymentSummaryAmount,
  type OfferPaymentStatusFilter,
} from '../../../../api/payments';
import { isOffPlatformPayment } from '../types';
import { StatusTip } from '../../../ui/Tooltip';
import { EmptyState } from '../../../ui/EmptyState';
import { paymentStatusTips, paymentTagTips } from '../../../../onboarding/copy';

const PAGE_SIZE = 20;

const STATUS_FILTERS: { label: string; value: OfferPaymentStatusFilter | '' }[] = [
  { label: 'All status', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Paid', value: 'paid' },
];

function normalize(value: string | undefined | null): string {
  return (value || '').trim().toLowerCase();
}

function isPayerRole(role: string | undefined | null): boolean {
  const r = normalize(role);
  if (r === 'earned') return false;
  if (r === 'spent' || r === 'paid') return true;
  return r === 'creator' || r === 'payer' || r === 'buyer' || r === 'sender';
}

function statusBadgeClass(status: string | undefined | null): string {
  switch (normalize(status)) {
    case 'succeeded':
    case 'paid':
    case 'completed':
    case 'confirmed':
      return 'bg-[#0d2818] text-[#4ade80] border border-[#1b432a]';
    case 'pending':
    case 'processing':
      return 'bg-[#2a2010] text-[#fbbf24] border border-[#4a3b1c]';
    case 'failed':
    case 'rejected':
      return 'bg-[#2d1416] text-[#f87171] border border-[#4c1d24]';
    case 'refunded':
      return 'bg-zinc-800 text-zinc-300 border border-zinc-700';
    default:
      return 'bg-[#182234] text-[#60a5fa] border border-[#253956]';
  }
}

function statusLabel(status: string | undefined | null): string {
  const s = normalize(status);
  switch (s) {
    case 'succeeded':
    case 'paid':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'refunded':
      return 'Refunded';
    default:
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown';
  }
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMoney(n: number, currency: string): string {
  return `${currency === 'USD' ? '$' : `${currency} `}${n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatMoneyList(entries: OfferPaymentSummaryAmount[]): string {
  if (entries.length === 0) return formatMoney(0, 'USD');
  return entries.map((e) => formatMoney(e.amount, e.currency)).join(' + ');
}

function StatCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: 'good' | 'bad' | 'info' | 'warn';
}) {
  const toneClasses: Record<typeof tone, string> = {
    good: 'bg-[#0d2818] text-[#4ade80] border-[#1b432a]',
    bad: 'bg-[#2d1416] text-[#f87171] border-[#4c1d24]',
    info: 'bg-[#182234] text-[#60a5fa] border-[#253956]',
    warn: 'bg-[#2a2010] text-[#fbbf24] border-[#4a3b1c]',
  };

  return (
    <div className="flex items-center justify-between rounded-2xl border border-[#22242a] bg-[#121316] p-5">
      <div>
        <span className="text-xs font-semibold text-zinc-500">{label}</span>
        <p className="mt-3 text-2xl font-bold text-white">{value}</p>
      </div>
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border p-3 ${toneClasses[tone]}`}
      >
        {icon}
      </span>
    </div>
  );
}

export const PaymentHistoryTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<OfferPaymentStatusFilter | ''>('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Debounce the search input before it hits the API.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 1000);
    return () => clearTimeout(handle);
  }, [search]);

  // Reset to page 1 whenever the active search term or status filter changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  // Close the status filter dropdown when clicking outside of it.
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isFilterOpen]);

  const { data, isLoading, isError } = useOfferPaymentsList({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
  });
  const csvExport = useOfferPaymentsCsvExport();
  const activeStatusLabel =
    STATUS_FILTERS.find((f) => f.value === statusFilter)?.label ?? 'All Status';

  const rows: OfferPaymentHistoryItem[] = useMemo(() => data?.results ?? [], [data]);

  const totalEarned = data?.summary.totalEarned ?? [];
  const totalSpent = data?.summary.totalSpent ?? [];
  const pendingPayout = data?.summary.pendingPayout ?? [];

  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (isLoading && !data) {
    return (
      <div className="rounded-3xl border border-[#22242a] p-12 text-center text-sm text-zinc-400">
        Loading payment history…
      </div>
    );
  }

  if (isError && rows.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-red-900/50 p-12 text-center">
        <h3 className="mb-1 text-lg font-bold text-white">Couldn't load payment history</h3>
        <p className="text-sm text-zinc-500">Please try again.</p>
      </div>
    );
  }

  if (rows.length === 0 && !debouncedSearch && !statusFilter) {
    return (
      <EmptyState
        icon={<FiInbox size={22} />}
        title="No payment history yet"
        description="Payments on your offers appear here, whether paid by card through Stripe or recorded as paid outside Musicade."
        tourId="hiring"
        className="rounded-3xl border border-white/10 bg-[#0A0C11]"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard
          label="Total Earned"
          value={formatMoneyList(totalEarned)}
          icon={<FiArrowUpRight size={16} />}
          tone="good"
        />
        <StatCard
          label="Total Spent"
          value={formatMoneyList(totalSpent)}
          icon={<FiArrowDownRight size={16} />}
          tone="bad"
        />
        <StatCard
          label="Pending Payout"
          value={formatMoneyList(pendingPayout)}
          icon={<FiClock size={16} />}
          tone="warn"
        />
      </div>

      <div className="rounded-2xl border border-[#22242a] bg-[#121316] p-5">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="relative w-full sm:max-w-xs">
            <FiSearch
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transactions..."
              className="w-full rounded-full border border-[#22242a] bg-[#0A0C11] py-2.5 pl-9 pr-4 text-xs text-white placeholder:text-zinc-500 outline-none focus:border-zinc-600"
            />
          </label>

          <div className="flex items-center gap-2">
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setIsFilterOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-[#22242a] px-4 py-2.5 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white cursor-pointer"
              >
                <FiFilter size={13} /> {activeStatusLabel}
                <FiChevronDown size={13} />
              </button>

              {isFilterOpen && (
                <div className="absolute left-0 z-10 mt-2 w-44 rounded-2xl border border-[#22242a] bg-[#121316] p-2 shadow-xl">
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.label}
                      onClick={() => {
                        setStatusFilter(f.value);
                        setIsFilterOpen(false);
                      }}
                      className={`block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition cursor-pointer ${
                        statusFilter === f.value
                          ? 'text-[#4ade80]'
                          : 'text-zinc-300 hover:bg-white/[0.04] hover:text-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() =>
                csvExport.mutate({
                  search: debouncedSearch || undefined,
                  status: statusFilter || undefined,
                })
              }
              disabled={csvExport.isPending}
              className="flex items-center gap-2 rounded-full border border-[#22242a] px-4 py-2.5 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              <FiDownload size={13} /> {csvExport.isPending ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr className="border-b border-[#22242a] text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-3 font-semibold">Transaction ID</th>
                <th className="px-3 py-3 font-semibold">Gig</th>
                <th className="px-3 py-3 font-semibold">Buyer</th>
                <th className="px-3 py-3 font-semibold">Seller</th>
                <th className="px-3 py-3 text-right font-semibold">Amount</th>
                <th className="px-3 py-3 text-right font-semibold">Fees</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 text-right font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const payerIsCreator = isPayerRole(r.role);
                const buyer = payerIsCreator ? 'You' : r.creator;
                const seller = payerIsCreator ? r.recipient : 'You';
                const offPlatform = isOffPlatformPayment(r);
                const hasFees = r.platformFeeAmount > 0 || r.processingFeeAmount != null;
                return (
                  <tr
                    key={r.id}
                    className="border-b border-[#1a1b1f] text-sm last:border-b-0 transition hover:bg-white/[0.02]"
                  >
                    <td className="px-3 py-4 font-mono text-xs text-zinc-500">{r.transactionId}</td>
                    <td className="px-3 py-4 font-semibold text-white">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{r.gigLabel || r.gigTitle || r.offerTitle}</span>
                        {offPlatform && (
                          <StatusTip content={paymentTagTips.offPlatform}>
                            <span className="inline-flex items-center rounded-full border border-[#3b2f5c] bg-[#1f1a2e] px-2 py-0.5 text-[10px] font-semibold text-[#c4b5fd]">
                              Off-platform
                            </span>
                          </StatusTip>
                        )}
                      </div>
                      {r.note && (
                        <p
                          className="mt-1 max-w-[260px] truncate text-xs font-normal text-zinc-500"
                          title={r.note}
                        >
                          {r.note}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-4 text-zinc-400">{buyer}</td>
                    <td className="px-3 py-4 text-zinc-400">{seller}</td>
                    <td className="px-3 py-4 text-right font-bold text-white">
                      {formatMoney(r.amount, r.currency)}
                    </td>
                    <td className="px-3 py-4 text-right text-xs text-zinc-400">
                      {offPlatform && !hasFees ? (
                        <div className="text-zinc-600">—</div>
                      ) : (
                        <>
                          <div>
                            Commission{' '}
                            {r.platformFeeAmount > 0
                              ? formatMoney(r.platformFeeAmount, r.currency)
                              : '—'}
                          </div>
                          {r.processingFeeAmount != null && (
                            <div className="mt-1">
                              Processing {formatMoney(r.processingFeeAmount, r.currency)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      <StatusTip content={paymentStatusTips[statusLabel(r.status).toLowerCase()]}>
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(r.status)}`}
                        >
                          {statusLabel(r.status)}
                        </span>
                      </StatusTip>
                    </td>
                    <td className="px-3 py-4 text-right text-zinc-500">
                      {formatDate(r.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {rows.length === 0 && (
            <div className="py-12 text-center text-sm text-zinc-500">
              {debouncedSearch
                ? `No transactions match "${debouncedSearch}".`
                : 'No transactions match this filter.'}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-between border-t border-[#22242a] pt-4">
            <span className="text-xs text-zinc-500">
              Page {page} of {totalPages} · {totalCount} transactions
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 rounded-full border border-[#22242a] px-3 py-2 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                <FiChevronLeft size={13} /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 rounded-full border border-[#22242a] px-3 py-2 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                Next <FiChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
